import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Alert,
  Button,
  Grid,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { FileInput } from '../NACCS'
import { useAPI } from '../../api'
import toFormData from 'json-form-data'
import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import {
  Column,
  DynamicDataSheetGrid,
  floatColumn,
  keyColumn,
  textColumn,
} from 'react-datasheet-grid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DATAGRID_ROW_HEIGHT,
  EXTRA_BOTTOM_MARGIN,
  FORM_DIV_HEIGHT,
} from '../Invoice'
import useWindowDimensions from '../../customHook/useWindowDimensions'
import productSelectColumn from '../Invoice/productColumn'
import MasterSelect from '../Invoice/MasterSelect'
import { TemlateListSearch } from '../PrintScreen/PrintList/TemplateListSearch'
import { AxiosError } from 'axios'
import usePrintStore from '../../store/printStore'
import { SheetError } from '../Invoice/types'
import { isEmpty } from 'lodash'
import { sortPrintTemplateDetails } from '../../utils/sortUtils'
import {
  filterSchema,
  sheetSchema,
  templateNameSchema,
} from './validationSchema'
import {
  OCRImportFormValues,
  PrintTemplateDetail,
  PrintTemplate,
  OCRProduct,
} from './types'
import { ContextMenu } from './contextMenu'
import { sumOfTotalCost } from '../../utils/sumOfToalCost'
import DataExportButton from '../reusableComponents/DataExportButton'

export default function OCRImport() {
  const api = useAPI()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { height } = useWindowDimensions()
  const [CSVImportData, setCSVImportData] = useState<PrintTemplateDetail[]>()
  const [filter, setFilter] = useState<OCRImportFormValues | null>(null)
  const [errors, setErrors] = useState<SheetError[]>([])
  const [templateError, setTemplateError] = useState<string | null>(null)
  const [templateName, setTemplateName] = useState<string>('')
  const { products, printTemplateId } = usePrintStore()
  const [templateSelected, setTemplateSelected] = useState(false)
  const [template, setTemplate] = useState<PrintTemplate | null>(null)
  const [totalAmountSum, setTotalAmountSum] = useState<string | number>('')
  const [format, setFormat] = useState<string>('En')
  const [key, setKey] = useState(0)

  // this function takes value from child component TemplateListSearch when the onChangeIs triggered
  const templateSelect = useCallback(
    (isTemplateSelected: boolean, template: PrintTemplate | null) => {
      if (isTemplateSelected && template) {
        setTemplateSelected(isTemplateSelected)
        setTemplate(template)
        setKey((prevKey) => prevKey + 1)
        setTemplateName(template.name)
      } else {
        setTemplate(null)
        setTemplateName('')
      }
    },
    []
  )
  useEffect(() => {
    if (filter && templateSelected && template) {
      const sortedData = sortPrintTemplateDetails(
        products,
        template,
        printTemplateId
      )
      const updatedItems: PrintTemplateDetail[] =
        sortedData.print_template_detail.map((item: PrintTemplateDetail) => ({
          product: item.product,
          quantity: item.quantity ? +item.quantity : null,
          unit_price: item.unit_price ? +item.unit_price : null,
          total_price: item.total_price ? +item.total_price : null,
          product_name_ocr: item.product_name_ocr || null,
          jan_code_ocr: item.jan_code_ocr || null,
          product_no_ocr: item.product_no_ocr || null,
          ocr_consignor: item.ocr_consignor || null,
        }))
      setCSVImportData(updatedItems)
      setTotalAmountSum(sumOfTotalCost(updatedItems))
      setErrors([])
      setTemplateError(null)
      setTemplateName(template.name || '')
    }
  }, [filter, products, templateSelected, template, printTemplateId])

  const form = useForm<OCRImportFormValues>({
    resolver: yupResolver(filterSchema) as any,
    defaultValues: {
      consignor: '',
      ocr_input: undefined,
    },
  })
  const importMutation = useMutation({
    mutationFn: (data: OCRImportFormValues): Promise<PrintTemplateDetail[]> => {
      return api.create('ocr-import', toFormData(data as any), true)
    },
    onSuccess: (data: PrintTemplateDetail[]) => {
      setFilter({
        consignor: data[0]?.ocr_consignor ?? undefined,
      })
      setCSVImportData(data)
      setTotalAmountSum(sumOfTotalCost(data))
      setTemplate(null)
      setErrors([])
      setTemplateError(null)
      setTemplateName(data[0]?.ocr_order_no ?? '')
      enqueueSnackbar(t('actions.ocrDataImport'), {
        variant: 'success',
      })
    },
    onError: (error: AxiosError) => {
      let errorMessage = ''
      if (error.response && error.response.data) {
        errorMessage =
          typeof error.response.data === 'object'
            ? Object.values(error.response.data)[0]
            : JSON.stringify(error.response.data)
      } else {
        errorMessage = error.message
      }
      enqueueSnackbar(`エラー : ${errorMessage}`, {
        variant: 'error',
      })
    },
  })

  const numberColumn = useMemo(
    () =>
      ({
        ...textColumn,
        valueViewer: ({ cellData }: { cellData: number | null }) => {
          return typeof cellData === 'number' ? cellData.toFixed(10) : cellData
        },
        valueEditor: ({
          cellData,
          onChange,
        }: {
          cellData: number | null
          onChange: (data: number | null) => void
        }) => {
          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = parseFloat(e.target.value)
            onChange(isNaN(newValue) ? null : newValue)
          }

          return (
            <input
              type="number"
              step="any"
              value={cellData ?? ''}
              onChange={handleChange}
            />
          )
        },
      }) as Partial<Column<number | null, any, string>>,
    []
  )

  const columns: Column<PrintTemplateDetail>[] = useMemo(
    () => [
      {
        ...keyColumn(
          'product',
          productSelectColumn({ filter: filter! }) as Column<
            Partial<OCRProduct>,
            any,
            string
          >
        ),
        title: t('ocrImports.fields.product'),
        basis: 250,
      },
      {
        ...keyColumn('unit_price', numberColumn),
        title: t('ocrImports.fields.unit_price'),
        basis: 140,
      },
      {
        ...keyColumn('quantity', numberColumn),
        title: t('ocrImports.fields.quantity'),
        basis: 140,
      },
      {
        ...keyColumn('total_price', numberColumn),
        title: `${t('ocrImports.fields.total_price')} ${totalAmountSum}`,
        basis: 140,
      },
      {
        ...keyColumn('jan_code_ocr', textColumn),
        title: t('ocrImports.fields.jan_code_ocr'),
        basis: 200,
      },
      {
        ...keyColumn('product_name_ocr', textColumn),
        title: t('ocrImports.fields.product_name_ocr'),
        basis: 210,
      },
      {
        ...keyColumn('product_no_ocr', textColumn),
        title: t('ocrImports.fields.product_no_ocr'),
        basis: 210,
      },
    ],
    [t, filter, totalAmountSum, numberColumn]
  )

  const handleFormatChange = (e: SelectChangeEvent) => {
    setFormat(e.target.value as string)
  }

  const onSubmit = form.handleSubmit((data) => {
    if (data.ocr_input) {
      importMutation.mutate(data)
    }
    setFilter(data)
  })

  const reset = () => {
    form.reset()
    setCSVImportData([])
    setErrors([])
    setTemplate(null)
    setTemplateError(null)
    setTemplateName('')
    setTotalAmountSum('')
  }

  const registerMutation = useMutation({
    mutationFn: (data: PrintTemplateDetail[]): Promise<PrintTemplate> => {
      return api.create<PrintTemplate>('print-template', {
        print_template_detail: data.map((item) => ({
          ...item,
          product_id: item.product.id,
        })),
        name: templateName.trim(),
        template_id: template ? template.id : null,
      })
    },
    onSuccess: (data: PrintTemplate) => {
      // use the data.id and data.name to set the template
      setTemplate(data)
      enqueueSnackbar(t('actions.ocrDataRegister'), {
        variant: 'success',
      })
    },
  })

  const register = async () => {
    let templateError = null
    let sheetErrors: SheetError[] = []

    // Validate templateName
    try {
      await templateNameSchema.validate(templateName.trim(), { abortEarly: false })
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        templateError = error.errors[0]
      }
    }
    try {
      await sheetSchema.validate(CSVImportData, { abortEarly: false })
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        sheetErrors = error.inner
          .map((e) => {
            if (e.type === 'rowError' && e.path) {
              const rowIndices = e.path.split(',').map((i) => parseInt(i) || 0)
              return rowIndices.map((r) => ({ row: r, message: e.message }))
            }
            const pathParts = e.path?.split('.') || []
            const row =
              pathParts.length > 0
                ? parseInt(pathParts[0].replace(/\D/g, '')) || 0
                : 0
            const column = pathParts.length > 1 ? pathParts[1] : undefined
            return { row, column, message: e.message }
          })
          .flat()
      }
    }
    setTemplateError(templateError)
    setErrors(sheetErrors)
    if (!templateError && sheetErrors.length === 0 && CSVImportData) {
      try {
        await registerMutation.mutateAsync(CSVImportData)
        if (!template) {
          setTemplateName('')
        }
      } catch (error) {
        enqueueSnackbar(t('messages.error'), { variant: 'error' })
      }
    }
  }

  const handleCellChange = (values: PrintTemplateDetail[]) => {
    const updatedData = values.map((item) => {
      const updatedItem = {
        ...item,
        total_price:
          item.unit_price && item.quantity
            ? parseFloat((item.unit_price * item.quantity).toFixed(10))
            : item.total_price
            ? item.total_price
            : null,
      }
      return updatedItem
    })

    setCSVImportData(updatedData)
    setTotalAmountSum(sumOfTotalCost(updatedData))
    setErrors([])
    setTemplateError(null)
  }

  return (
    <Stack spacing={4}>
      <Typography variant="h6">{t('ocrImports.title')}</Typography>
      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <Stack spacing={2}>
            <Grid
              container
              spacing={1}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
            >
              <Grid item xs={3}>
                <FileInput
                  name="ocr_input"
                  label={t('ocrImports.file_input')}
                  accept=".csv"
                  placeholder={t('ocrImports.file_input_placeholder')}
                  disabled={form.formState.isSubmitSuccessful}
                />
                {form.formState.errors.ocr_input && (
                  <Typography color="error" variant="caption">
                    {form.formState.errors.ocr_input.message}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={3}>
                <MasterSelect
                  resource="consignors"
                  getLabel={(c) => `${c.japanese_name} / ${c.english_name}`}
                  name="consignor"
                  width={600}
                  height={FORM_DIV_HEIGHT}
                />
              </Grid>
              <Grid item xs={1} display="flex">
                <Button variant="contained" fullWidth type="submit">
                  {t('ocrImports.start')}
                </Button>
              </Grid>
              <Grid item xs={1} display="flex">
                <Button variant="contained" fullWidth onClick={reset}>
                  {t('actions.clear')}
                </Button>
              </Grid>
              {form.formState.isSubmitSuccessful && (
                <Grid item xs={3} sx={{ ml: 'auto' }} display="flex">
                  <TemlateListSearch
                    filter={filter}
                    onChangeIndicator={templateSelect}
                    isPrintScreen={false}
                  />
                </Grid>
              )}
            </Grid>
          </Stack>
        </form>
      </FormProvider>
      {form.formState.isSubmitSuccessful && (
        <>
          <DynamicDataSheetGrid
            value={CSVImportData}
            columns={columns}
            onChange={handleCellChange}
            rowHeight={DATAGRID_ROW_HEIGHT}
            height={height - FORM_DIV_HEIGHT - EXTRA_BOTTOM_MARGIN}
            contextMenuComponent={(props) => <ContextMenu {...props} />}
          />
          {!isEmpty(errors) && (
            <Stack>
              {errors.map((e) => (
                <Alert severity="error" key={`${e.row}-${e.column}`}>
                  <span>{e.row + 1}行目：</span>
                  {e.message}
                </Alert>
              ))}
            </Stack>
          )}
          <Stack flexDirection="row-reverse" gap={1}>
            <Button variant="contained" onClick={register}>
              {t('actions.create')}
            </Button>
            <TextField
              key={key}
              value={templateName}
              placeholder={t('ocrImports.template_name')}
              label={t('ocrImports.template_name')}
              onChange={(e) => {
                setTemplateName(e.target.value)
                setTemplateError(null)
              }}
            />
            <DataExportButton
              data={CSVImportData}
              filter={filter}
              fileName={
                templateName.trim() ||
                form.watch('ocr_input')?.name?.replace(/\.[^/.]+$/, '') ||
                ''
              }
              format={format}
              handleFormatChange={handleFormatChange}
            />
          </Stack>
          {templateError && (
            <Stack
              width="fit-content"
              flexDirection="row-reverse"
              alignSelf="flex-end"
            >
              <Alert severity="error">
                <span>{templateError}</span>
              </Alert>
            </Stack>
          )}
        </>
      )}
    </Stack>
  )
}
